import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../Components/Navbar';
import Footer from '../Components/Footer';
import apiService from '../apiService';
import { Helmet } from 'react-helmet';

function Home() {
    const navigate = useNavigate();
    const [categories, setCategories] = useState([]);
    const [services, setServices] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [activeTab, setActiveTab] = useState('All');
    const [bookingSuccessModal, setBookingSuccessModal] = useState(null);

    // Fallback curated categories if backend is slow or offline
    const fallbackCategories = [
        {
            category_id: 177,
            name: "Repairs & Service",
            slug: "repairs-&-service",
            price: "199",
            icon: "https://images.unsplash.com/photo-1581092160607-ee22621dd758?auto=format&fit=crop&w=400&q=80",
            badge: "Most Popular"
        },
        {
            category_id: 223,
            name: "Transportation",
            slug: "transportation",
            price: "49",
            icon: "https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?auto=format&fit=crop&w=400&q=80",
            badge: "Fast Cab"
        },
        {
            category_id: 231,
            name: "Beauty & Salon",
            slug: "beauty-care",
            price: "249",
            icon: "https://images.unsplash.com/photo-1560066984-138dadb4c035?auto=format&fit=crop&w=400&q=80",
            badge: "Trending"
        },
        {
            category_id: 297,
            name: "Fashion & Lifestyle",
            slug: "fashion",
            price: "149",
            icon: "https://images.unsplash.com/photo-1489987707025-afc232f7ea0f?auto=format&fit=crop&w=400&q=80",
            badge: "New"
        },
        {
            category_id: 263,
            name: "Financial Services",
            slug: "finance",
            price: "0",
            icon: "https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?auto=format&fit=crop&w=400&q=80",
            badge: "Instant Approval"
        },
        {
            category_id: 282,
            name: "Jobs & Employment",
            slug: "jobs",
            price: "0",
            icon: "https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?auto=format&fit=crop&w=400&q=80",
            badge: "Hiring"
        },
        {
            category_id: 302,
            name: "Mobile & Gadget Repair",
            slug: "mobile",
            price: "149",
            icon: "https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?auto=format&fit=crop&w=400&q=80",
            badge: "Same Day"
        },
        {
            category_id: 279,
            name: "Food & Groceries",
            slug: "food-order",
            price: "99",
            icon: "https://images.unsplash.com/photo-1504674900247-0877df9cc836?auto=format&fit=crop&w=400&q=80",
            badge: "30 Min"
        }
    ];

    const defaultCategoryImages = {
        'repairs': 'https://images.unsplash.com/photo-1581092160607-ee22621dd758?auto=format&fit=crop&w=600&q=80',
        'transport': 'https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?auto=format&fit=crop&w=600&q=80',
        'beauty': 'https://images.unsplash.com/photo-1560066984-138dadb4c035?auto=format&fit=crop&w=600&q=80',
        'cleaning': 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?auto=format&fit=crop&w=600&q=80',
        'fashion': 'https://images.unsplash.com/photo-1489987707025-afc232f7ea0f?auto=format&fit=crop&w=600&q=80',
        'health': 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&w=600&q=80',
        'finance': 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?auto=format&fit=crop&w=600&q=80',
        'jobs': 'https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?auto=format&fit=crop&w=600&q=80',
        'rent': 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=600&q=80',
        'default': 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?auto=format&fit=crop&w=600&q=80'
    };

    const categorizeService = (title = '') => {
        const lower = title.toLowerCase();
        if (lower.includes('clean') || lower.includes('pest') || lower.includes('sanitize') || lower.includes('maid') || lower.includes('housekeep')) {
            return 'Cleaning';
        }
        if (lower.includes('beauty') || lower.includes('salon') || lower.includes('spa') || lower.includes('hair') || lower.includes('facial') || lower.includes('makeup')) {
            return 'Beauty';
        }
        if (lower.includes('transport') || lower.includes('cab') || lower.includes('driver') || lower.includes('car') || lower.includes('bike') || lower.includes('ride') || lower.includes('tour')) {
            return 'Transport';
        }
        if (lower.includes('repair') || lower.includes('electric') || lower.includes('plumb') || lower.includes('tv') || lower.includes('ac') || lower.includes('appliance') || lower.includes('mobile')) {
            return 'Repairs';
        }
        return 'Repairs';
    };

    const normalizeService = (svc, index = 0) => {
        const title = svc.title || svc.name || 'Professional Doorstep Service';
        const category = svc.category || categorizeService(title);

        let image = svc.image || svc.icon;
        if (image && typeof image === 'string') {
            if (!image.startsWith('http')) {
                image = `http://127.0.0.1:8000/storage/${image}`;
            }
        } else {
            const catKey = category.toLowerCase();
            image = defaultCategoryImages[catKey] || defaultCategoryImages['default'];
        }

        const price = (svc.price && String(svc.price).trim() !== '' && svc.price !== '0')
            ? String(svc.price)
            : String(149 + (index * 50));

        const seed = ((svc.id || index + 1) * 7) % 18;
        const rating = svc.rating || (4.81 + seed / 100).toFixed(2);
        const reviews = svc.reviews || (380 + (seed * 85));

        let time = svc.time;
        if (!time) {
            if (title.toLowerCase().includes('clean')) time = '60-90 mins';
            else if (title.toLowerCase().includes('tv') || title.toLowerCase().includes('ac') || title.toLowerCase().includes('repair')) time = '45 mins';
            else if (title.toLowerCase().includes('cab') || title.toLowerCase().includes('transport')) time = 'Instant';
            else time = '30 mins';
        }

        return {
            id: svc.id || (index + 100),
            title,
            name: title,
            category,
            price,
            rating,
            reviews,
            image,
            icon: image,
            time,
            slug: svc.slug || title.toLowerCase().replace(/[^a-z0-9]+/g, '-')
        };
    };

    const curatedServices = [
        {
            id: 101,
            title: "Split & Window AC Complete Servicing",
            category: "Repairs",
            price: 499,
            rating: 4.89,
            reviews: 1250,
            image: "https://images.unsplash.com/photo-1621905251189-08b45d6a269e?auto=format&fit=crop&w=500&q=80",
            time: "45 mins"
        },
        {
            id: 102,
            title: "LED / Smart TV Screen & Board Repair",
            category: "Repairs",
            price: 349,
            rating: 4.82,
            reviews: 840,
            image: "https://images.unsplash.com/photo-1593784991095-a205069470b6?auto=format&fit=crop&w=500&q=80",
            time: "60 mins"
        },
        {
            id: 103,
            title: "Deep Home Cleaning & Sanitization",
            category: "Cleaning",
            price: 899,
            rating: 4.93,
            reviews: 2100,
            image: "https://images.unsplash.com/photo-1581578731548-c64695cc6952?auto=format&fit=crop&w=500&q=80",
            time: "120 mins"
        },
        {
            id: 104,
            title: "Salon Classic Facial & Hair Spa at Home",
            category: "Beauty",
            price: 599,
            rating: 4.91,
            reviews: 1680,
            image: "https://images.unsplash.com/photo-1560066984-138dadb4c035?auto=format&fit=crop&w=500&q=80",
            time: "60 mins"
        },
        {
            id: 105,
            title: "Emergency Electrician & Wiring Fix",
            category: "Repairs",
            price: 199,
            rating: 4.85,
            reviews: 3200,
            image: "https://images.unsplash.com/photo-1621905252507-b35492cc74b4?auto=format&fit=crop&w=500&q=80",
            time: "30 mins"
        },
        {
            id: 106,
            title: "City Car Rental & Driver on Demand",
            category: "Transport",
            price: 450,
            rating: 4.79,
            reviews: 950,
            image: "https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?auto=format&fit=crop&w=500&q=80",
            time: "Instant"
        }
    ];

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const res = await apiService.getcategories();
                if (res?.data?.data && Array.isArray(res.data.data) && res.data.data.length > 0) {
                    setCategories(res.data.data);
                } else {
                    setCategories(fallbackCategories);
                }
            } catch (err) {
                console.warn("Using curated categories fallback:", err?.message || err);
                setCategories(fallbackCategories);
            }

            try {
                const svcRes = await apiService.get_services_data();
                if (svcRes?.data?.data?.popular_services?.length > 0) {
                    const normalized = svcRes.data.data.popular_services.map((item, idx) => normalizeService(item, idx));
                    setServices(normalized);
                } else {
                    setServices(curatedServices.map((item, idx) => normalizeService(item, idx)));
                }
            } catch (err) {
                console.warn("Using curated services fallback:", err?.message || err);
                setServices(curatedServices.map((item, idx) => normalizeService(item, idx)));
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, []);

    const handleSearch = (e) => {
        if (e) e.preventDefault();
        if (searchQuery.trim()) {
            navigate(`/services?search=${encodeURIComponent(searchQuery.trim())}`);
        } else {
            navigate('/services');
        }
    };

    const handleQuickBook = (item) => {
        const storedCart = localStorage.getItem('cart');
        const cartItems = storedCart ? JSON.parse(storedCart) : [];
        const existingIndex = cartItems.findIndex(i => i.id === item.id);

        if (existingIndex > -1) {
            cartItems[existingIndex].quantity = (cartItems[existingIndex].quantity || 1) + 1;
        } else {
            cartItems.push({
                id: item.id,
                service_name: item.title || item.name,
                price: item.price,
                quantity: 1,
                image: item.image || item.icon
            });
        }

        localStorage.setItem('cart', JSON.stringify(cartItems));
        window.dispatchEvent(new Event('cartUpdated'));

        setBookingSuccessModal({
            title: item.title || item.name,
            price: item.price
        });
    };

    // Combine loaded API services with curated service items to provide comprehensive coverage
    const allAvailableServices = services.length > 0
        ? [...services, ...curatedServices.map((item, idx) => normalizeService(item, idx + 50))]
        : curatedServices.map((item, idx) => normalizeService(item, idx));

    // Deduplicate by title
    const uniqueServices = Array.from(new Map(allAvailableServices.map(item => [item.title.toLowerCase(), item])).values());

    const filteredServices = activeTab === 'All'
        ? uniqueServices
        : uniqueServices.filter(s => s.category.toLowerCase() === activeTab.toLowerCase());

    return (
        <div style={{ backgroundColor: '#ffffff', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
            <Helmet>
                <title>Reliep - India's Everyday Services Platform | Book in 30 Mins</title>
                <meta name="description" content="Book verified experts for home repairs, cleaning, beauty, transport, and fashion. Fast doorstep service in 30 minutes with 100% Reliep Guarantee." />
            </Helmet>

            <Navbar />

            {/* HERO SECTION */}
            <section style={{
                position: 'relative',
                background: 'radial-gradient(circle at 85% 20%, #fbf0fb 0%, #ffffff 60%)',
                padding: '60px 20px 80px',
                overflow: 'hidden',
                borderBottom: '1px solid #f2e4f2'
            }}>
                <div style={{
                    maxWidth: '1280px',
                    margin: '0 auto',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                    gap: '48px',
                    alignItems: 'center'
                }}>
                    {/* Left Column: Headlines & Search */}
                    <div>
                        <div style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            background: '#fbf2fb',
                            border: '1px solid #ebd0eb',
                            padding: '6px 14px',
                            borderRadius: '9999px',
                            fontSize: '0.82rem',
                            fontWeight: 700,
                            color: '#850C85',
                            marginBottom: '20px'
                        }}>
                            <span>⭐</span>
                            <span>#1 Hyperlocal Everyday Services Platform</span>
                        </div>

                        <h1 style={{
                            fontSize: 'clamp(2.2rem, 4.5vw, 3.4rem)',
                            fontWeight: 800,
                            color: '#0f172a',
                            lineHeight: 1.15,
                            letterSpacing: '-1px',
                            marginBottom: '18px'
                        }}>
                            Expert Services at Your Doorstep,{' '}
                            <span style={{
                                color: '#850C85',
                                background: 'linear-gradient(135deg, #850C85 0%, #b81ab8 100%)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent'
                            }}>
                                In Just 30 Mins.
                            </span>
                        </h1>

                        <p style={{
                            fontSize: '1.05rem',
                            lineHeight: 1.6,
                            color: '#475569',
                            marginBottom: '32px',
                            maxWidth: '560px'
                        }}>
                            From AC repair and electrical fixes to home deep-cleaning, salon at home, and ride booking — certified experts at transparent upfront prices.
                        </p>

                        {/* Interactive Hero Search Card */}
                        <form onSubmit={handleSearch} style={{
                            background: '#ffffff',
                            borderRadius: '16px',
                            padding: '8px',
                            boxShadow: '0 12px 35px rgba(133, 12, 133, 0.12)',
                            border: '1.5px solid #ebd0eb',
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '8px',
                            marginBottom: '24px',
                            maxWidth: '620px'
                        }}>
                            <div style={{
                                flex: '1',
                                minWidth: '200px',
                                display: 'flex',
                                alignItems: 'center',
                                padding: '8px 14px',
                                gap: '10px'
                            }}>
                                <span style={{ fontSize: '1.2rem', color: '#850C85' }}>🔍</span>
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Search 'AC Repair', 'Salon', 'Plumber'..."
                                    style={{
                                        border: 'none',
                                        outline: 'none',
                                        width: '100%',
                                        fontSize: '0.98rem',
                                        color: '#0f172a',
                                        fontWeight: 500
                                    }}
                                />
                            </div>

                            <button
                                type="submit"
                                style={{
                                    background: 'linear-gradient(135deg, #850C85 0%, #9e0f9e 100%)',
                                    color: '#ffffff',
                                    border: 'none',
                                    borderRadius: '12px',
                                    padding: '12px 28px',
                                    fontSize: '1rem',
                                    fontWeight: 700,
                                    cursor: 'pointer',
                                    boxShadow: '0 6px 18px rgba(133, 12, 133, 0.3)',
                                    transition: 'all 0.2s ease',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                }}
                            >
                                <span>Find Expert</span>
                                <span>→</span>
                            </button>
                        </form>

                        {/* Quick Service Chips */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                            <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#64748b' }}>
                                Popular:
                            </span>
                            {['AC Repair', 'Electrician', 'Plumber', 'Salon', 'Cleaning', 'Fashion', 'Transport'].map((chip) => (
                                <Link
                                    key={chip}
                                    to={`/services?search=${encodeURIComponent(chip)}`}
                                    style={{
                                        fontSize: '0.82rem',
                                        fontWeight: 600,
                                        color: '#850C85',
                                        background: '#fbf2fb',
                                        border: '1px solid #ebd0eb',
                                        padding: '4px 12px',
                                        borderRadius: '9999px',
                                        textDecoration: 'none',
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    {chip}
                                </Link>
                            ))}
                        </div>

                        {/* Social Proof Strip */}
                        <div style={{
                            marginTop: '36px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '20px',
                            flexWrap: 'wrap'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <div style={{ display: 'flex', marginLeft: '6px' }}>
                                    {['🧑‍🔧', '👩‍💼', '👨‍💻', '👩‍🦰'].map((emoji, idx) => (
                                        <div key={idx} style={{
                                            width: '32px',
                                            height: '32px',
                                            borderRadius: '50%',
                                            background: '#fbf2fb',
                                            border: '2px solid #ffffff',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            marginLeft: '-8px',
                                            fontSize: '1rem'
                                        }}>
                                            {emoji}
                                        </div>
                                    ))}
                                </div>
                                <div style={{ marginLeft: '12px' }}>
                                    <span style={{ fontWeight: 800, color: '#0f172a', fontSize: '0.9rem', display: 'block' }}>
                                        ★ 4.9 Rated
                                    </span>
                                    <span style={{ fontSize: '0.75rem', color: '#64748b' }}>50,000+ Happy Customers</span>
                                </div>
                            </div>

                            <div style={{ height: '28px', width: '1px', background: '#e2e8f0' }} className="hidden sm:block"></div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontSize: '1.2rem', color: '#850C85' }}>🛡️</span>
                                <div>
                                    <span style={{ fontWeight: 800, color: '#0f172a', fontSize: '0.9rem', display: 'block' }}>
                                        100% Guaranteed
                                    </span>
                                    <span style={{ fontSize: '0.75rem', color: '#64748b' }}>Free re-work on issues</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Column: Hero Visual Showcase */}
                    <div style={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
                        {/* Glow Backdrop */}
                        <div style={{
                            position: 'absolute',
                            width: '380px',
                            height: '380px',
                            borderRadius: '50%',
                            background: 'radial-gradient(circle, rgba(133, 12, 133, 0.15) 0%, rgba(255, 255, 255, 0) 70%)',
                            zIndex: 0
                        }}></div>

                        {/* Main Featured Showcase Card */}
                        <div style={{
                            background: '#ffffff',
                            borderRadius: '24px',
                            boxShadow: '0 25px 50px -12px rgba(133, 12, 133, 0.18)',
                            border: '1.5px solid #f1e2f1',
                            padding: '24px',
                            width: '100%',
                            maxWidth: '440px',
                            position: 'relative',
                            zIndex: 1
                        }}>
                            <div style={{
                                width: '100%',
                                height: '230px',
                                borderRadius: '16px',
                                overflow: 'hidden',
                                position: 'relative',
                                marginBottom: '18px'
                            }}>
                                <img
                                    src="https://images.unsplash.com/photo-1581578731548-c64695cc6952?auto=format&fit=crop&w=700&q=80"
                                    alt="Professional Doorstep Service"
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                                <div style={{
                                    position: 'absolute',
                                    top: '12px',
                                    left: '12px',
                                    background: 'rgba(255, 255, 255, 0.95)',
                                    backdropFilter: 'blur(4px)',
                                    padding: '5px 12px',
                                    borderRadius: '9999px',
                                    fontSize: '0.78rem',
                                    fontWeight: 700,
                                    color: '#850C85',
                                    boxShadow: '0 4px 10px rgba(0,0,0,0.1)'
                                }}>
                                    ⚡ Live in Your City
                                </div>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                                <div>
                                    <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: '#0f172a', margin: '0 0 4px' }}>
                                        Complete Home Maintenance
                                    </h3>
                                    <p style={{ fontSize: '0.82rem', color: '#64748b', margin: 0 }}>
                                        AC, Electrical, Plumbing & Deep Cleaning
                                    </p>
                                </div>
                                <span style={{
                                    fontSize: '1.15rem',
                                    fontWeight: 800,
                                    color: '#850C85',
                                    background: '#fbf2fb',
                                    padding: '4px 10px',
                                    borderRadius: '8px'
                                }}>
                                    ₹299
                                </span>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                                <span style={{ color: '#f59e0b', fontSize: '0.9rem' }}>★★★★★</span>
                                <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#0f172a' }}>4.92</span>
                                <span style={{ fontSize: '0.78rem', color: '#94a3b8' }}>(2,430 verified reviews)</span>
                            </div>

                            <Link
                                to="/services"
                                style={{
                                    display: 'block',
                                    textAlign: 'center',
                                    background: '#850C85',
                                    color: '#ffffff',
                                    padding: '12px',
                                    borderRadius: '12px',
                                    fontWeight: 700,
                                    textDecoration: 'none',
                                    fontSize: '0.92rem',
                                    boxShadow: '0 4px 14px rgba(133, 12, 133, 0.3)'
                                }}
                            >
                                Book Now in 30 Seconds
                            </Link>

                            {/* Floating Badge: Pro Verified */}
                            <div style={{
                                position: 'absolute',
                                bottom: '-20px',
                                left: '-20px',
                                background: '#ffffff',
                                padding: '10px 16px',
                                borderRadius: '14px',
                                boxShadow: '0 12px 28px rgba(0,0,0,0.12)',
                                border: '1px solid #f1e2f1',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px'
                            }}>
                                <div style={{
                                    width: '32px',
                                    height: '32px',
                                    borderRadius: '50%',
                                    background: '#e6f7ea',
                                    color: '#28a745',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontWeight: 800,
                                    fontSize: '1rem'
                                }}>
                                    ✓
                                </div>
                                <div>
                                    <span style={{ fontSize: '0.8rem', fontWeight: 800, color: '#0f172a', display: 'block' }}>
                                        Verified Technicians
                                    </span>
                                    <span style={{ fontSize: '0.7rem', color: '#64748b' }}>Aadhaar & Police Verified</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* BROWSE ALL CATEGORIES SECTION */}
            <section style={{ padding: '70px 20px', backgroundColor: '#faf8fa' }}>
                <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-end',
                        marginBottom: '36px',
                        flexWrap: 'wrap',
                        gap: '16px'
                    }}>
                        <div>
                            <span style={{
                                fontSize: '0.82rem',
                                fontWeight: 800,
                                color: '#850C85',
                                letterSpacing: '1px',
                                textTransform: 'uppercase',
                                display: 'block',
                                marginBottom: '6px'
                            }}>
                                CATEGORIES
                            </span>
                            <h2 style={{ fontSize: '2rem', fontWeight: 800, color: '#0f172a', margin: 0 }}>
                                Explore All Everyday Services
                            </h2>
                        </div>
                        <Link
                            to="/services"
                            style={{
                                color: '#850C85',
                                textDecoration: 'none',
                                fontWeight: 700,
                                fontSize: '0.95rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                            }}
                        >
                            <span>View All Categories</span>
                            <span>→</span>
                        </Link>
                    </div>

                    {/* Category Cards Grid */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                        gap: '20px'
                    }}>
                        {categories.slice(0, 8).map((cat, idx) => {
                            const iconUrl = cat.icon
                                ? (cat.icon.startsWith('http') ? cat.icon : `http://127.0.0.1:8000/storage/${cat.icon}`)
                                : fallbackCategories[idx % fallbackCategories.length].icon;

                            return (
                                <Link
                                    key={cat.category_id || idx}
                                    to={`/categories/${cat.slug || 'repairs-&-service'}`}
                                    style={{
                                        textDecoration: 'none',
                                        backgroundColor: '#ffffff',
                                        borderRadius: '16px',
                                        overflow: 'hidden',
                                        border: '1px solid #f1e2f1',
                                        boxShadow: '0 4px 14px rgba(133, 12, 133, 0.05)',
                                        transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                                        display: 'flex',
                                        flexDirection: 'column'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-6px)';
                                        e.currentTarget.style.borderColor = '#850C85';
                                        e.currentTarget.style.boxShadow = '0 16px 32px rgba(133, 12, 133, 0.12)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.borderColor = '#f1e2f1';
                                        e.currentTarget.style.boxShadow = '0 4px 14px rgba(133, 12, 133, 0.05)';
                                    }}
                                >
                                    <div style={{
                                        width: '100%',
                                        height: '170px',
                                        position: 'relative',
                                        overflow: 'hidden',
                                        backgroundColor: '#fbf2fb'
                                    }}>
                                        <img
                                            src={iconUrl}
                                            alt={cat.name}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                                transition: 'transform 0.4s ease'
                                            }}
                                            onError={(e) => {
                                                e.target.src = fallbackCategories[idx % fallbackCategories.length].icon;
                                            }}
                                        />
                                        <div style={{
                                            position: 'absolute',
                                            bottom: '10px',
                                            left: '10px',
                                            background: 'rgba(255, 255, 255, 0.95)',
                                            backdropFilter: 'blur(4px)',
                                            padding: '4px 10px',
                                            borderRadius: '6px',
                                            fontSize: '0.75rem',
                                            fontWeight: 700,
                                            color: '#850C85'
                                        }}>
                                            Starts ₹{cat.price || '99'}
                                        </div>
                                    </div>

                                    <div style={{ padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <div>
                                            <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#0f172a', margin: '0 0 4px' }}>
                                                {cat.name}
                                            </h3>
                                            <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                                                Available in your area
                                            </span>
                                        </div>
                                        <div style={{
                                            width: '32px',
                                            height: '32px',
                                            borderRadius: '50%',
                                            background: '#fbf2fb',
                                            color: '#850C85',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontWeight: 'bold',
                                            fontSize: '0.9rem'
                                        }}>
                                            →
                                        </div>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* FEATURED & POPULAR SERVICES SECTION */}
            <section style={{ padding: '80px 20px', backgroundColor: '#ffffff' }}>
                <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
                    <div style={{ textAlign: 'center', maxWidth: '640px', margin: '0 auto 40px' }}>
                        <span style={{
                            fontSize: '0.82rem',
                            fontWeight: 800,
                            color: '#850C85',
                            letterSpacing: '1px',
                            textTransform: 'uppercase',
                            display: 'block',
                            marginBottom: '6px'
                        }}>
                            TRENDING BOOKINGS
                        </span>
                        <h2 style={{ fontSize: '2.2rem', fontWeight: 800, color: '#0f172a', marginBottom: '12px' }}>
                            Most Booked Services Right Now
                        </h2>
                        <p style={{ fontSize: '0.95rem', color: '#64748b' }}>
                            Top-rated professional services delivered at your doorstep with verified pricing.
                        </p>
                    </div>

                    {/* Filter Tabs */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        gap: '10px',
                        marginBottom: '36px',
                        flexWrap: 'wrap'
                    }}>
                        {['All', 'Repairs', 'Cleaning', 'Beauty', 'Transport'].map((tab) => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                style={{
                                    padding: '8px 20px',
                                    borderRadius: '9999px',
                                    border: activeTab === tab ? '1.5px solid #850C85' : '1px solid #ebd0eb',
                                    background: activeTab === tab ? '#850C85' : '#ffffff',
                                    color: activeTab === tab ? '#ffffff' : '#475569',
                                    fontWeight: 700,
                                    fontSize: '0.88rem',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    boxShadow: activeTab === tab ? '0 4px 12px rgba(133, 12, 133, 0.25)' : 'none'
                                }}
                            >
                                {tab}
                            </button>
                        ))}
                    </div>

                    {/* Services Grid */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                        gap: '24px'
                    }}>
                        {filteredServices.map((svc) => (
                            <div
                                key={svc.id}
                                style={{
                                    backgroundColor: '#ffffff',
                                    borderRadius: '16px',
                                    border: '1px solid #f1e2f1',
                                    overflow: 'hidden',
                                    boxShadow: '0 4px 16px rgba(133, 12, 133, 0.05)',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    transition: 'all 0.3s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-6px)';
                                    e.currentTarget.style.boxShadow = '0 16px 32px rgba(133, 12, 133, 0.12)';
                                    e.currentTarget.style.borderColor = '#850C85';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(133, 12, 133, 0.05)';
                                    e.currentTarget.style.borderColor = '#f1e2f1';
                                }}
                            >
                                <div style={{ height: '190px', width: '100%', position: 'relative', backgroundColor: '#fbf2fb' }}>
                                    <img
                                        src={svc.image}
                                        alt={svc.title}
                                        onError={(e) => {
                                            const catKey = (svc.category || 'default').toLowerCase();
                                            e.currentTarget.src = defaultCategoryImages[catKey] || defaultCategoryImages['default'];
                                        }}
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                    <div style={{
                                        position: 'absolute',
                                        top: '12px',
                                        right: '12px',
                                        background: 'rgba(255, 255, 255, 0.95)',
                                        backdropFilter: 'blur(4px)',
                                        padding: '4px 10px',
                                        borderRadius: '9999px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                        fontSize: '0.8rem',
                                        fontWeight: 800,
                                        color: '#0f172a',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                    }}>
                                        <span style={{ color: '#f59e0b' }}>★</span>
                                        <span>{svc.rating}</span>
                                        <span style={{ fontSize: '0.7rem', color: '#64748b' }}>({svc.reviews || '500+'})</span>
                                    </div>
                                    <div style={{
                                        position: 'absolute',
                                        bottom: '12px',
                                        left: '12px',
                                        background: '#850C85',
                                        color: '#ffffff',
                                        padding: '4px 10px',
                                        borderRadius: '6px',
                                        fontSize: '0.72rem',
                                        fontWeight: 700,
                                        letterSpacing: '0.5px'
                                    }}>
                                        {svc.category}
                                    </div>
                                </div>

                                <div style={{ padding: '20px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                                    <span style={{
                                        fontSize: '0.75rem',
                                        fontWeight: 800,
                                        color: '#850C85',
                                        textTransform: 'uppercase',
                                        marginBottom: '6px'
                                    }}>
                                        ⏱ {svc.time} • Verified Expert
                                    </span>

                                    <h3 style={{
                                        fontSize: '1.08rem',
                                        fontWeight: 700,
                                        color: '#0f172a',
                                        margin: '0 0 14px',
                                        lineHeight: 1.35
                                    }}>
                                        {svc.title}
                                    </h3>

                                    <div style={{
                                        marginTop: 'auto',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        paddingTop: '14px',
                                        borderTop: '1px solid #f1e2f1'
                                    }}>
                                        <div>
                                            <span style={{ fontSize: '0.75rem', color: '#64748b', display: 'block' }}>Starts at</span>
                                            <span style={{ fontSize: '1.3rem', fontWeight: 800, color: '#850C85' }}>
                                                ₹{svc.price}
                                            </span>
                                        </div>

                                        <button
                                            onClick={() => handleQuickBook(svc)}
                                            style={{
                                                background: '#850C85',
                                                color: '#ffffff',
                                                border: 'none',
                                                borderRadius: '10px',
                                                padding: '10px 18px',
                                                fontWeight: 700,
                                                fontSize: '0.88rem',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s',
                                                boxShadow: '0 4px 12px rgba(133, 12, 133, 0.25)'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.background = '#6d0a6d';
                                                e.currentTarget.style.transform = 'translateY(-1px)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.background = '#850C85';
                                                e.currentTarget.style.transform = 'translateY(0)';
                                            }}
                                        >
                                            Book Now
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* WHY CHOOSE RELIEP SECTION */}
            <section style={{
                padding: '80px 20px',
                background: 'linear-gradient(135deg, #fbf2fb 0%, #f7e6f7 100%)',
                borderTop: '1px solid #f1d5f1',
                borderBottom: '1px solid #f1d5f1'
            }}>
                <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
                    <div style={{ textAlign: 'center', maxWidth: '640px', margin: '0 auto 50px' }}>
                        <span style={{
                            fontSize: '0.82rem',
                            fontWeight: 800,
                            color: '#850C85',
                            letterSpacing: '1px',
                            textTransform: 'uppercase',
                            display: 'block',
                            marginBottom: '6px'
                        }}>
                            THE RELIEP ADVANTAGE
                        </span>
                        <h2 style={{ fontSize: '2.2rem', fontWeight: 800, color: '#0f172a', marginBottom: '12px' }}>
                            Why 50,000+ Customers Trust Reliep
                        </h2>
                        <p style={{ fontSize: '0.95rem', color: '#64748b' }}>
                            We take the stress out of daily home tasks with transparent, reliable service guarantees.
                        </p>
                    </div>

                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                        gap: '24px'
                    }}>
                        {[
                            {
                                icon: '🛡️',
                                title: '100% Verified Experts',
                                desc: 'Every technician undergoes strict background verification, skill assessments, and police checks.'
                            },
                            {
                                icon: '⚡',
                                title: '30-Minute Arrival',
                                desc: 'Our smart hyperlocal engine assigns the closest available technician for rapid doorstep turnaround.'
                            },
                            {
                                icon: '💳',
                                title: 'Transparent Pricing',
                                desc: 'Know exact rates upfront before booking starts. No surprise bills or hidden inspection charges.'
                            },
                            {
                                icon: '✨',
                                title: 'Reliep Guarantee',
                                desc: 'Not 100% happy with your service? We offer free re-work or instant hassle-free refund.'
                            }
                        ].map((item, idx) => (
                            <div
                                key={idx}
                                style={{
                                    backgroundColor: '#ffffff',
                                    borderRadius: '16px',
                                    padding: '30px 24px',
                                    border: '1px solid #f1e2f1',
                                    boxShadow: '0 8px 24px rgba(133, 12, 133, 0.06)'
                                }}
                            >
                                <div style={{
                                    width: '54px',
                                    height: '54px',
                                    borderRadius: '14px',
                                    background: '#fbf2fb',
                                    color: '#850C85',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '1.8rem',
                                    marginBottom: '18px'
                                }}>
                                    {item.icon}
                                </div>
                                <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: '#0f172a', marginBottom: '10px' }}>
                                    {item.title}
                                </h3>
                                <p style={{ fontSize: '0.88rem', color: '#64748b', lineHeight: 1.6, margin: 0 }}>
                                    {item.desc}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* HOW IT WORKS SECTION */}
            <section style={{ padding: '80px 20px', backgroundColor: '#ffffff' }}>
                <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
                    <div style={{ textAlign: 'center', marginBottom: '50px' }}>
                        <span style={{
                            fontSize: '0.82rem',
                            fontWeight: 800,
                            color: '#850C85',
                            letterSpacing: '1px',
                            textTransform: 'uppercase',
                            display: 'block',
                            marginBottom: '6px'
                        }}>
                            SIMPLE PROCESS
                        </span>
                        <h2 style={{ fontSize: '2.2rem', fontWeight: 800, color: '#0f172a' }}>
                            How Reliep Works In 3 Steps
                        </h2>
                    </div>

                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                        gap: '32px',
                        position: 'relative'
                    }}>
                        {[
                            {
                                step: '01',
                                title: 'Select Service & Location',
                                desc: 'Choose from 100+ services and enter your locality or pin code.'
                            },
                            {
                                step: '02',
                                title: 'Schedule Your Slot',
                                desc: 'Pick an instant 30-min slot or schedule convenient time as per your schedule.'
                            },
                            {
                                step: '03',
                                title: 'Sit Back & Pay Safely',
                                desc: 'Verified pro arrives at your door. Pay securely only after complete satisfaction.'
                            }
                        ].map((step, idx) => (
                            <div
                                key={idx}
                                style={{
                                    textAlign: 'center',
                                    padding: '24px',
                                    background: '#faf8fa',
                                    borderRadius: '16px',
                                    border: '1px solid #f1e2f1'
                                }}
                            >
                                <div style={{
                                    width: '60px',
                                    height: '60px',
                                    borderRadius: '50%',
                                    background: '#850C85',
                                    color: '#ffffff',
                                    fontWeight: 800,
                                    fontSize: '1.4rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '0 auto 18px',
                                    boxShadow: '0 8px 20px rgba(133, 12, 133, 0.28)'
                                }}>
                                    {step.step}
                                </div>
                                <h3 style={{ fontSize: '1.2rem', fontWeight: 800, color: '#0f172a', marginBottom: '8px' }}>
                                    {step.title}
                                </h3>
                                <p style={{ fontSize: '0.88rem', color: '#64748b', lineHeight: 1.6 }}>
                                    {step.desc}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* BECOME A PARTNER HIGH-CONVERSION CTA BANNER */}
            <section style={{ padding: '40px 20px 80px' }}>
                <div style={{
                    maxWidth: '1280px',
                    margin: '0 auto',
                    background: 'linear-gradient(135deg, #850C85 0%, #520652 100%)',
                    borderRadius: '24px',
                    padding: '60px 40px',
                    color: '#ffffff',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                    gap: '40px',
                    alignItems: 'center',
                    boxShadow: '0 25px 50px -12px rgba(133, 12, 133, 0.4)',
                    position: 'relative',
                    overflow: 'hidden'
                }}>
                    <div>
                        <div style={{
                            display: 'inline-block',
                            background: 'rgba(255, 255, 255, 0.15)',
                            padding: '6px 14px',
                            borderRadius: '9999px',
                            fontSize: '0.82rem',
                            fontWeight: 700,
                            marginBottom: '16px'
                        }}>
                            💼 Grow Your Business Pan India
                        </div>

                        <h2 style={{
                            fontSize: 'clamp(1.8rem, 3.5vw, 2.6rem)',
                            fontWeight: 800,
                            lineHeight: 1.2,
                            color: '#ffffff',
                            marginBottom: '16px'
                        }}>
                            Earn up to ₹45,000/Month as a Verified Reliep Partner
                        </h2>

                        <p style={{
                            fontSize: '1rem',
                            color: '#f3d5f3',
                            lineHeight: 1.6,
                            marginBottom: '28px',
                            maxWidth: '520px'
                        }}>
                            Join 5,000+ local electricians, technicians, salons, and store owners who get verified customer orders daily with instant payout to bank account.
                        </p>

                        <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
                            <Link
                                to="/become-a-partner"
                                style={{
                                    background: '#ffffff',
                                    color: '#850C85',
                                    padding: '14px 28px',
                                    borderRadius: '12px',
                                    fontWeight: 800,
                                    fontSize: '1rem',
                                    textDecoration: 'none',
                                    boxShadow: '0 4px 14px rgba(0,0,0,0.2)',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}
                            >
                                <span>Register as Partner</span>
                                <span>→</span>
                            </Link>

                            <Link
                                to="/partner/chandan-electronics"
                                style={{
                                    background: 'rgba(255,255,255,0.12)',
                                    color: '#ffffff',
                                    border: '1px solid rgba(255,255,255,0.3)',
                                    padding: '14px 24px',
                                    borderRadius: '12px',
                                    fontWeight: 700,
                                    fontSize: '0.95rem',
                                    textDecoration: 'none'
                                }}
                            >
                                View Partner Store Demo
                            </Link>
                        </div>
                    </div>

                    {/* Partner Stats Card */}
                    <div style={{
                        background: 'rgba(255, 255, 255, 0.1)',
                        backdropFilter: 'blur(10px)',
                        borderRadius: '20px',
                        padding: '30px',
                        border: '1px solid rgba(255, 255, 255, 0.2)'
                    }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                            <div style={{ borderRight: '1px solid rgba(255,255,255,0.2)', paddingRight: '15px' }}>
                                <span style={{ fontSize: '2rem', fontWeight: 800, display: 'block' }}>5,000+</span>
                                <span style={{ fontSize: '0.82rem', color: '#e2b5e2' }}>Active Service Partners</span>
                            </div>
                            <div>
                                <span style={{ fontSize: '2rem', fontWeight: 800, display: 'block' }}>₹0</span>
                                <span style={{ fontSize: '0.82rem', color: '#e2b5e2' }}>Zero Registration Fee</span>
                            </div>
                            <div style={{ borderRight: '1px solid rgba(255,255,255,0.2)', paddingRight: '15px', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '15px' }}>
                                <span style={{ fontSize: '2rem', fontWeight: 800, display: 'block' }}>Instant</span>
                                <span style={{ fontSize: '0.82rem', color: '#e2b5e2' }}>Bank Payouts</span>
                            </div>
                            <div style={{ borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '15px' }}>
                                <span style={{ fontSize: '2rem', fontWeight: 800, display: 'block' }}>24/7</span>
                                <span style={{ fontSize: '0.82rem', color: '#e2b5e2' }}>Partner Care</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* QUICK BOOKING SUCCESS MODAL */}
            {bookingSuccessModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    backgroundColor: 'rgba(0, 0, 0, 0.65)',
                    backdropFilter: 'blur(4px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 99999,
                    padding: '16px'
                }}>
                    <div style={{
                        background: '#ffffff',
                        borderRadius: '16px',
                        width: '100%',
                        maxWidth: '460px',
                        padding: '28px',
                        textAlign: 'center',
                        boxShadow: '0 20px 40px rgba(0,0,0,0.25)',
                        position: 'relative'
                    }}>
                        <div style={{
                            width: '64px',
                            height: '64px',
                            borderRadius: '50%',
                            background: '#fbf2fb',
                            color: '#850C85',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '2rem',
                            margin: '0 auto 16px',
                            border: '2px solid #850C85'
                        }}>
                            ✓
                        </div>

                        <h3 style={{ fontSize: '1.3rem', fontWeight: 800, color: '#0f172a', marginBottom: '8px' }}>
                            Added to Your Cart!
                        </h3>
                        <p style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '20px' }}>
                            <strong>{bookingSuccessModal.title}</strong> (₹{bookingSuccessModal.price}) has been added to your cart.
                        </p>

                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button
                                onClick={() => setBookingSuccessModal(null)}
                                style={{
                                    flex: 1,
                                    padding: '12px',
                                    borderRadius: '10px',
                                    border: '1px solid #cbd5e1',
                                    background: '#f8fafc',
                                    color: '#475569',
                                    fontWeight: 600,
                                    cursor: 'pointer'
                                }}
                            >
                                Continue Browsing
                            </button>
                            <button
                                onClick={() => navigate('/cart')}
                                style={{
                                    flex: 1,
                                    padding: '12px',
                                    borderRadius: '10px',
                                    border: 'none',
                                    background: '#850C85',
                                    color: '#fff',
                                    fontWeight: 700,
                                    cursor: 'pointer',
                                    boxShadow: '0 4px 12px rgba(133, 12, 133, 0.3)'
                                }}
                            >
                                View Cart & Checkout
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <Footer />
        </div>
    );
}

export default Home;
